import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Send } from 'lucide-react';
import { usePortal } from './PortalApp';
import { apiError, fmtDateTime, portalApi } from './portalApi';
import { Card, Empty, PageHeader, Spinner } from './portalUi';

export default function PortalMessages() {
  const { me } = usePortal();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await portalApi.get('/messages'); setData(r.data); } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [data?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const r = await portalApi.post('/messages', { text: t });
      setData((d) => ({ ...d, messages: [...(d?.messages || []), r.data] }));
      setText('');
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!data) return <Spinner />;
  const team = data.team || [];
  return (
    <div data-testid="portal-messages">
      <PageHeader overline="Message your PM" title="Direct line to your project team" subtitle="Messages go straight to the 365 project managers and dispatch. They reply here — and if anything is still unread at the end of the day, everyone on your team with a portal login gets one summary email." />
      <Card className="flex flex-col" style={{ height: 'min(70vh, 720px)' }}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap" data-testid="portal-team">
          <span className="p-overline">Your 365 team</span>
          {team.length ? team.map((p) => <span key={p.name} className="inline-flex items-center gap-1.5 text-sm text-slate-700" data-testid={`portal-team-${p.name.replace(/\s+/g, '-').toLowerCase()}`}><span className="h-6 w-6 rounded-full bg-[#0B192C] text-white text-[10px] font-bold inline-flex items-center justify-center">{p.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}</span> {p.name}<span className="text-xs text-slate-400">· {p.role}</span></span>) : <span className="text-sm text-slate-500">Project managers & dispatch</span>}
        </div>
        <div className="flex-1 overflow-y-auto p-scroll px-5 py-4 space-y-3 bg-[#F8FAFC]" data-testid="message-list">
          {data.messages.length === 0 ? <Empty icon={MessageSquare} title="No messages yet" text="Ask about a dispatch, an invoice or an hour on the timesheet — we answer here." /> : data.messages.map((m) => {
            const mine = m.sender_type === 'customer';
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 shadow-sm ${mine ? 'bg-[#0B192C] text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-md'}`}>
                  <div className={`text-[11px] font-bold mb-0.5 ${mine ? 'text-blue-200' : 'text-blue-700'}`}>{mine ? (m.sender_id === me?.user?.id ? 'You' : m.sender_name) : `${m.sender_name || '365'} · 365 Industrial`}</div>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[10px] p-mono mt-1 ${mine ? 'text-blue-200/70' : 'text-slate-400'}`}>{fmtDateTime(m.created_at)}</div>
                </div>
              </div>);
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 items-end">
          <textarea className="p-input resize-none" rows={2} placeholder="Type a message to your PM…" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="message-input" />
          <button type="submit" className="p-btn p-btn-primary shrink-0" disabled={busy || !text.trim()} data-testid="message-send-btn"><Send className="w-4 h-4" /> Send</button>
        </form>
      </Card>
    </div>
  );
}
